// Charges panel — list of counts with grade, max exposure, and
// immigration-impact flag (AGGRAVATED FELONY / CIMT visually screams).
// Reads matter.workspace.charges (array of ChargeShape).

import type { ChargeShape } from "../workspace-shapes";

interface Props {
  charges: ChargeShape[] | undefined;
}

function impactTone(impact: string | undefined): { bg: string; fg: string } {
  if (!impact) return { bg: "var(--line-2)", fg: "var(--muted)" };
  const upper = impact.toUpperCase();
  if (upper.includes("AGGRAVATED")) return { bg: "var(--red-600)", fg: "#fff" };
  if (upper.includes("CIMT")) return { bg: "var(--warn-50)", fg: "var(--warn-600)" };
  return { bg: "var(--line-2)", fg: "var(--muted)" };
}

export function ChargesPanel({ charges }: Props) {
  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">Charges</div>
        <div className="cc-card-sub">
          · {charges?.length ?? 0} count{(charges?.length ?? 0) === 1 ? "" : "s"}
        </div>
      </div>
      {!charges || charges.length === 0 ? (
        <div style={{ padding: "16px 18px", color: "var(--muted)", fontSize: 12.5 }}>
          No charges entered yet.
        </div>
      ) : (
        <div style={{ padding: "8px 18px 14px" }}>
          {charges.map((c, i) => {
            const tone = impactTone(c.immigrationImpact);
            return (
              <div
                key={i}
                style={{
                  padding: "10px 0",
                  borderBottom: i === charges.length - 1 ? "none" : "1px solid var(--line-2)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 8,
                    marginBottom: 4,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--muted)",
                    }}
                  >
                    Count {c.count}
                  </span>
                  {c.grade && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: "var(--line-2)",
                        color: "var(--ink-2)",
                      }}
                    >
                      {c.grade}
                    </span>
                  )}
                  {c.immigrationImpact && (
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: 4,
                        background: tone.bg,
                        color: tone.fg,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {c.immigrationImpact}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, fontWeight: 550, color: "var(--ink)" }}>{c.charge}</div>
                {(c.maxJail || c.maxFine) && (
                  <div
                    style={{
                      marginTop: 4,
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 10.5,
                      color: "var(--muted)",
                    }}
                  >
                    {c.maxJail ? `Max ${c.maxJail}` : ""}
                    {c.maxJail && c.maxFine ? " · " : ""}
                    {c.maxFine ? `Fine ${c.maxFine}` : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
